import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Menu, X, Search, Mail, ChevronDown } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { ThemeToggle } from '@/components/ThemeToggle';

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bestOfOpen, setBestOfOpen] = useState(false);
  const bestOfRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (bestOfRef.current && !bestOfRef.current.contains(e.target as Node)) {
        setBestOfOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-white dark:bg-gray-900 shadow-sm dark:shadow-gray-800">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="/lovable-uploads/cbcb9241-13c5-4453-b79d-961c777edd77.png" 
              alt="Pinnacle Buy Logo" 
              className="h-10 w-auto"
              style={{ filter: 'brightness(0) saturate(100%) invert(17%) sepia(89%) saturate(3136%) hue-rotate(208deg) brightness(95%) contrast(98%)' }}
            />
          </Link>

          {/* Navigation - Desktop */}
          {!isMobile && (
            <nav className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-gray-700 dark:text-gray-200 hover:text-brand-blue dark:hover:text-brand-blue font-medium special-underline">
                Home
              </Link>
              <Link to="/compare" className="text-gray-700 dark:text-gray-200 hover:text-brand-blue dark:hover:text-brand-blue font-medium special-underline">
                Compare Firms
              </Link>

              {/* Best Of dropdown */}
              <div className="relative" ref={bestOfRef}>
                <button
                  onClick={() => setBestOfOpen(!bestOfOpen)}
                  className="text-gray-700 dark:text-gray-200 hover:text-brand-blue dark:hover:text-brand-blue font-medium special-underline flex items-center gap-1"
                >
                  Best Of
                  <ChevronDown className={`h-4 w-4 transition-transform ${bestOfOpen ? 'rotate-180' : ''}`} />
                </button>
                {bestOfOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 py-2">
                    <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">By Account Size</div>
                    <Link to="/best/25k-funded-account" onClick={() => setBestOfOpen(false)} className="block px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50">Best $25K Accounts</Link>
                    <Link to="/best/50k-funded-account" onClick={() => setBestOfOpen(false)} className="block px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50">Best $50K Accounts</Link>
                    <Link to="/best/100k-funded-account" onClick={() => setBestOfOpen(false)} className="block px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50">Best $100K Accounts</Link>
                    <Link to="/best/150k-funded-account" onClick={() => setBestOfOpen(false)} className="block px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50">Best $150K Accounts</Link>

                    <div className="border-t border-gray-200 dark:border-gray-700 my-1.5" />
                    <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">By Drawdown Type</div>
                    <Link to="/best/eod-drawdown-prop-firms" onClick={() => setBestOfOpen(false)} className="block px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50">EOD Drawdown Firms</Link>
                    <Link to="/best/trailing-drawdown-prop-firms" onClick={() => setBestOfOpen(false)} className="block px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50">Trailing Drawdown Firms</Link>
                    <Link to="/best/static-drawdown-prop-firms" onClick={() => setBestOfOpen(false)} className="block px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50">Static Drawdown Firms</Link>

                    <div className="border-t border-gray-200 dark:border-gray-700 my-1.5" />
                    <div className="px-3 py-1.5 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Other</div>
                    <Link to="/best/cheapest-prop-firms" onClick={() => setBestOfOpen(false)} className="block px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50">Cheapest Firms</Link>
                    <Link to="/best/no-activation-fee-prop-firms" onClick={() => setBestOfOpen(false)} className="block px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50">No Activation Fee</Link>
                    <Link to="/best/highest-profit-split-prop-firms" onClick={() => setBestOfOpen(false)} className="block px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50">Highest Profit Split</Link>
                    <Link to="/best/no-consistency-rule-prop-firms" onClick={() => setBestOfOpen(false)} className="block px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50">No Consistency Rule</Link>
                  </div>
                )}
              </div>

              <Link to="/blog" className="text-gray-700 dark:text-gray-200 hover:text-brand-blue dark:hover:text-brand-blue font-medium special-underline">
                Blog
              </Link>
              <Link to="/tools/drawdown-simulator" className="text-gray-700 dark:text-gray-200 hover:text-brand-blue dark:hover:text-brand-blue font-medium special-underline">
                Tools
              </Link>
            </nav>
          )}

          {/* Actions */}
          <div className="flex items-center space-x-3">
            {!isMobile && <ThemeToggle />}
            
            <Button variant="ghost" size="icon" className="text-gray-700 dark:text-gray-200">
              <Search className="h-5 w-5" />
            </Button>
            
            {!isMobile && (
              <Button variant="outline" className="hidden md:flex dark:border-gray-700 dark:text-gray-200">
                <Mail className="mr-2 h-4 w-4" />
                Get Updates
              </Button>
            )}
            
            <Button asChild className="bg-brand-blue hover:bg-brand-darkBlue">
              <Link to="/compare">
                Compare Now
              </Link>
            </Button>
            
            {/* Mobile menu button */}
            {isMobile && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden text-gray-700 dark:text-gray-200"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobile && mobileMenuOpen && (
        <div className="md:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex flex-col space-y-4">
              <Link 
                to="/" 
                className="text-gray-700 dark:text-gray-200 py-2 px-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/compare"
                className="text-gray-700 dark:text-gray-200 py-2 px-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
                onClick={() => setMobileMenuOpen(false)}
              >
                Compare Firms
              </Link>

              {/* Best Of - mobile */}
              <div className="py-1 px-3">
                <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-1">Best Of</div>
                <div className="grid grid-cols-2 gap-1 pl-2">
                  <Link to="/best/50k-funded-account" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 dark:text-gray-300 py-1 hover:text-brand-blue">$50K Accounts</Link>
                  <Link to="/best/100k-funded-account" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 dark:text-gray-300 py-1 hover:text-brand-blue">$100K Accounts</Link>
                  <Link to="/best/cheapest-prop-firms" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 dark:text-gray-300 py-1 hover:text-brand-blue">Cheapest Firms</Link>
                  <Link to="/best/eod-drawdown-prop-firms" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 dark:text-gray-300 py-1 hover:text-brand-blue">EOD Drawdown</Link>
                </div>
              </div>

              <Link
                to="/blog"
                className="text-gray-700 dark:text-gray-200 py-2 px-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
                onClick={() => setMobileMenuOpen(false)}
              >
                Blog
              </Link>
              <Link
                to="/tools/drawdown-simulator"
                className="text-gray-700 dark:text-gray-200 py-2 px-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
                onClick={() => setMobileMenuOpen(false)}
              >
                Tools
              </Link>
              <div className="flex items-center justify-between">
                <Button variant="outline" className="w-full justify-start dark:border-gray-700 dark:text-gray-200">
                  <Mail className="mr-2 h-4 w-4" />
                  Get Updates
                </Button>
                <div className="ml-4">
                  <ThemeToggle />
                </div>
              </div>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};
